import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, MapPin, ShieldCheck } from "lucide-react";

const COMPANY_TYPES = ["Pvt Ltd", "Public Ltd", "LLP", "Partnership", "NGO", "Trust", "Proprietary"];
const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];
const PAYMENT_METHODS = ["Cash", "Cheque", "UPI", "Bank Transfer"];
const SOURCES = ["Direct Online", "Volunteer", "Camp", "Center"];

function Required() { return <span className="text-rose-600">*</span>; }

export default function SubscriberForm() {
  const { id, type } = useParams(); // type = individual|organization (only on /new/:type)
  const isEdit = !!id;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [pers, setPers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [pubId, setPubId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const initType = (type === "organization" ? "Organization" : "Individual");

  const { register, handleSubmit, watch, setValue, control, reset, formState: { errors } } = useForm({
    defaultValues: {
      subscriber_type: initType,
      periodical_id: params.get("periodical_id") || "",
      subscription_plan_id: params.get("plan_id") || "",
      first_name: "", middle_name: "", last_name: "",
      gender: "", birthdate: "", anniversary_date: "", profession: "",
      company_name: "", incorporation_date: "", company_type: "Pvt Ltd", gst_number: "",
      contacts: [{ full_name: "", designation: "", mobile: "", email: "" }],
      mobile_no: "", is_whatsapp: true, whatsapp_no: "",
      email: "", opt_e_copy: false, opt_sms_whatsapp: true,
      address_line_1: "", address_line_2: "", landmark: "",
      pincode: "", city: "", district: "", state: "",
      address_verified: false,
      source: "", referrer_name: "", referrer_mobile: "", occasion: "",
      postage_class: "Registered Periodical",
      payment_method: "Cash", payment_amount: 0, cheque_number: "", cheque_bank: "", upi_ref: "",
      receipt_base64: null,
    },
  });

  const { fields: contactFields, append: appendContact, remove: removeContact } = useFieldArray({ control, name: "contacts" });
  const v = watch();

  // Load orgs once
  useEffect(() => { (async () => { try { const { data } = await api.get("/organizations/active-list"); setOrgs(data); } catch { /* */ } })(); }, []);
  useEffect(() => { if (!orgId) { setPubs([]); return; } (async () => { try { const { data } = await api.get("/publication-houses/active-list", { params: { organization_id: orgId } }); setPubs(data); } catch {} })(); }, [orgId]);
  useEffect(() => { if (!pubId) { setPers([]); return; } (async () => { try { const { data } = await api.get("/periodicals", { params: { publication_house_id: pubId, page: 1, page_size: 100 } }); setPers(data.items.filter((x) => x.status === "active")); } catch {} })(); }, [pubId]);
  useEffect(() => {
    if (!v.periodical_id) { setPlans([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/subscription-plans", { params: { periodical_id: v.periodical_id } });
        setPlans(data.filter((p) => (p.effective_status || p.status) === "active"));
      } catch {}
    })();
  }, [v.periodical_id]);

  // If we have presetPer, hydrate hierarchy
  useEffect(() => {
    const presetPer = params.get("periodical_id");
    if (!presetPer || isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/periodicals/${presetPer}`);
        const pub = await api.get(`/publication-houses/${data.publication_house_id}`);
        setOrgId(pub.data.organization_id);
        setPubId(data.publication_house_id);
      } catch {}
    })();
  }, [params, isEdit]);

  // Edit mode: hydrate
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/subscribers/${id}`);
        const per = await api.get(`/periodicals/${data.periodical_id}`);
        const pub = await api.get(`/publication-houses/${per.data.publication_house_id}`);
        setOrgId(pub.data.organization_id); setPubId(per.data.publication_house_id);
        reset({ ...data, contacts: data.contacts && data.contacts.length ? data.contacts : [{ full_name: "", designation: "", mobile: "", email: "" }], receipt_base64: null });
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    })();
  }, [id, isEdit, reset]);

  const lookupPincode = useCallback(async (pin) => {
    if (!/^[1-9]\d{5}$/.test(pin)) return;
    try {
      const { data } = await api.get(`/subscribers-meta/pincode/${pin}`);
      if (data.state) setValue("state", data.state);
      if (data.city && !v.city) setValue("city", data.city);
    } catch {}
  }, [setValue, v.city]);

  const onReceipt = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Receipt must be < 2MB"); return; }
    const r = new FileReader(); r.onload = () => setValue("receipt_base64", r.result); r.readAsDataURL(file);
  };

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      // Cleanup empty strings → null where appropriate
      const payload = { ...values };
      ["middle_name","gender","birthdate","anniversary_date","profession","company_name","incorporation_date","company_type","gst_number","whatsapp_no","email","address_line_2","landmark","district","source","referrer_name","referrer_mobile","occasion","cheque_number","cheque_bank","upi_ref"].forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (payload.subscriber_type === "Individual") {
        payload.contacts = [];
      } else {
        payload.contacts = (payload.contacts || []).filter((c) => c && c.full_name);
      }
      payload.payment_amount = Number(payload.payment_amount || 0);
      if (isEdit) {
        await api.put(`/subscribers/${id}`, payload);
        toast.success("Subscriber updated");
      } else {
        const res = await api.post("/subscribers", payload);
        toast.success(`Subscriber created · ${res.data.subscriber_code}`);
      }
      navigate("/subscribers");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const isOrg = v.subscriber_type === "Organization";

  return (
    <div data-testid="subscriber-form-page" className="mx-auto max-w-5xl">
      <Button variant="ghost" size="sm" asChild className="mb-3 rounded-sm text-slate-600">
        <Link to="/subscribers"><ArrowLeft size={14} className="mr-1" />Back</Link>
      </Button>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
        {isEdit ? "Edit Subscriber" : `New ${isOrg ? "Organizational" : "Individual"} Subscriber`}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6 rounded-sm border border-slate-200 bg-white p-6">
        <Section title="Subscription Hierarchy">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <Field label="Organization" required>
              <Select value={orgId} onValueChange={(val) => { setOrgId(val); setPubId(""); setValue("periodical_id", ""); setValue("subscription_plan_id", ""); }} disabled={isEdit}>
                <SelectTrigger className="rounded-sm" data-testid="sub-form-org"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Publication House" required>
              <Select value={pubId} onValueChange={(val) => { setPubId(val); setValue("periodical_id", ""); setValue("subscription_plan_id", ""); }} disabled={!orgId || isEdit}>
                <SelectTrigger className="rounded-sm" data-testid="sub-form-pub"><SelectValue placeholder={orgId ? "Select" : "—"} /></SelectTrigger>
                <SelectContent>{pubs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Periodical" required>
              <Select value={v.periodical_id} onValueChange={(val) => { setValue("periodical_id", val); setValue("subscription_plan_id", ""); }} disabled={!pubId || isEdit}>
                <SelectTrigger className="rounded-sm" data-testid="sub-form-per"><SelectValue placeholder={pubId ? "Select" : "—"} /></SelectTrigger>
                <SelectContent>{pers.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Plan" required>
              <Select value={v.subscription_plan_id} onValueChange={(val) => setValue("subscription_plan_id", val)} disabled={!v.periodical_id || isEdit}>
                <SelectTrigger className="rounded-sm" data-testid="sub-form-plan"><SelectValue placeholder={v.periodical_id ? "Select plan" : "—"} /></SelectTrigger>
                <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.plan_name} · {p.total_months}m · {p.currency || "INR"} {p.price}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-4 rounded-sm bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <span className="font-semibold">Auto:</span> Start date = 1st of next month · End date = Start + plan duration
            </div>
          </div>
        </Section>

        {!isOrg ? (
          <Section title="Personal Info">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="First Name" required><Input data-testid="sub-fname" {...register("first_name")} className="rounded-sm" /></Field>
              <Field label="Middle Name"><Input data-testid="sub-mname" {...register("middle_name")} className="rounded-sm" /></Field>
              <Field label="Last Name" required><Input data-testid="sub-lname" {...register("last_name")} className="rounded-sm" /></Field>
              <Field label="Gender"><Select value={v.gender || ""} onValueChange={(x) => setValue("gender", x)}><SelectTrigger data-testid="sub-gender" className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Birthdate"><Input type="date" data-testid="sub-birthdate" {...register("birthdate")} className="rounded-sm" /></Field>
              <Field label="Anniversary Date"><Input type="date" data-testid="sub-anniversary" {...register("anniversary_date")} className="rounded-sm" /></Field>
              <Field label="Profession / Education"><Input data-testid="sub-profession" {...register("profession")} className="rounded-sm" placeholder="e.g. Teacher, Engineer" /></Field>
            </div>
          </Section>
        ) : (
          <Section title="Company Info">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="Company Name" required full><Input data-testid="sub-company" {...register("company_name")} className="rounded-sm" /></Field>
              <Field label="Incorporation Date"><Input type="date" data-testid="sub-incdate" {...register("incorporation_date")} className="rounded-sm" max={new Date().toISOString().slice(0,10)} /></Field>
              <Field label="Company Type">
                <Select value={v.company_type || ""} onValueChange={(x) => setValue("company_type", x)}>
                  <SelectTrigger data-testid="sub-comptype" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMPANY_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="GST Number" hint="Enables Input Tax Credit"><Input data-testid="sub-gst" {...register("gst_number")} className="rounded-sm font-mono" placeholder="22AAAAA0000A1Z5" /></Field>
            </div>
            <div className="mt-6">
              <div className="mb-2 flex items-center justify-between">
                <Label className="text-sm font-medium text-slate-700">Contact Persons <Required /></Label>
                <Button type="button" size="sm" variant="outline" onClick={() => appendContact({ full_name: "", designation: "", mobile: "", email: "" })} data-testid="sub-add-contact" className="rounded-sm"><Plus size={14} className="mr-1" />Add</Button>
              </div>
              <div className="space-y-3">
                {contactFields.map((f, idx) => (
                  <div key={f.id} className="grid grid-cols-1 gap-2 rounded-sm border border-slate-200 p-3 sm:grid-cols-9" data-testid={`sub-contact-${idx}`}>
                    <Input placeholder="Full name" {...register(`contacts.${idx}.full_name`)} className="rounded-sm sm:col-span-3" />
                    <Input placeholder="Designation" {...register(`contacts.${idx}.designation`)} className="rounded-sm sm:col-span-2" />
                    <Input placeholder="Mobile" {...register(`contacts.${idx}.mobile`)} className="rounded-sm font-mono sm:col-span-2" />
                    <Input placeholder="Email" type="email" {...register(`contacts.${idx}.email`)} className="rounded-sm sm:col-span-2" />
                    {contactFields.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeContact(idx)} className="h-8 w-8 rounded-sm text-rose-600 hover:bg-rose-50 sm:col-start-9"><Trash2 size={14} /></Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}

        <Section title="Communication">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Mobile Number" required><Input data-testid="sub-mobile" {...register("mobile_no")} className="rounded-sm font-mono" placeholder="9876543210" /></Field>
            <div className="flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 sm:col-span-2">
              <div>
                <div className="text-sm font-medium text-slate-900">Mobile is on WhatsApp</div>
                <div className="text-xs text-slate-500">Toggle off to provide a separate WhatsApp number.</div>
              </div>
              <Switch data-testid="sub-iswhatsapp" checked={!!v.is_whatsapp} onCheckedChange={(b) => setValue("is_whatsapp", b)} />
            </div>
            {!v.is_whatsapp && (
              <Field label="WhatsApp Number"><Input data-testid="sub-whatsapp" {...register("whatsapp_no")} className="rounded-sm font-mono" placeholder="9876543210" /></Field>
            )}
            <Field label={`Email${v.opt_e_copy ? " (required for E-Copy)" : ""}`}><Input type="email" data-testid="sub-email" {...register("email")} className="rounded-sm" /></Field>
            <div className="flex items-center gap-2 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2">
              <Checkbox checked={!!v.opt_e_copy} onCheckedChange={(b) => setValue("opt_e_copy", b)} data-testid="sub-opt-ecopy" />
              <span className="text-sm text-slate-700">Receive E-Copy (Email)</span>
            </div>
            <div className="flex items-center gap-2 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2">
              <Checkbox checked={!!v.opt_sms_whatsapp} onCheckedChange={(b) => setValue("opt_sms_whatsapp", b)} data-testid="sub-opt-sms" />
              <span className="text-sm text-slate-700">Receive SMS/WhatsApp updates</span>
            </div>
          </div>
        </Section>

        <Section title="Postage-Ready Address">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Address Line 1 (Flat / House No, Building)" required full>
              <Input data-testid="sub-addr1" {...register("address_line_1")} className="rounded-sm" placeholder="Flat 4B, Sai Sadan" />
            </Field>
            <Field label="Address Line 2 (Street / Area)" full>
              <Input data-testid="sub-addr2" {...register("address_line_2")} className="rounded-sm" placeholder="Plot 12, Vidyanagar" />
            </Field>
            <Field label="Landmark"><Input data-testid="sub-landmark" {...register("landmark")} className="rounded-sm" placeholder="Near Shivaji Park" /></Field>
            <Field label="Pincode" required>
              <Input data-testid="sub-pincode" {...register("pincode", { onBlur: (e) => lookupPincode(e.target.value) })} className="rounded-sm font-mono" placeholder="400028" maxLength={6} />
            </Field>
            <Field label="City" required><Input data-testid="sub-city" {...register("city")} className="rounded-sm" /></Field>
            <Field label="District"><Input data-testid="sub-district" {...register("district")} className="rounded-sm" /></Field>
            <Field label="State" required><Input data-testid="sub-state" {...register("state")} className="rounded-sm" /></Field>
            <div className="flex items-center gap-2 rounded-sm border border-slate-200 bg-emerald-50/50 px-3 py-2 sm:col-span-3">
              <Checkbox checked={!!v.address_verified} onCheckedChange={(b) => setValue("address_verified", b)} data-testid="sub-addr-verified" />
              <ShieldCheck size={14} className="text-emerald-600" />
              <span className="text-sm text-slate-700">Address verified by volunteer (recommended for high-cost deliveries)</span>
            </div>
            <div className="sm:col-span-3 flex items-center gap-2 rounded-sm border border-dashed border-indigo-200 bg-indigo-50/50 px-3 py-2 text-xs text-[#002FA7]">
              <MapPin size={14} /> Pincode auto-fills State (and City where mapped). Override anytime.
            </div>
          </div>
        </Section>

        <Section title="Source & Occasion">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Reference Source">
              <Select value={v.source || ""} onValueChange={(x) => setValue("source", x)}>
                <SelectTrigger data-testid="sub-source" className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Referrer Name"><Input data-testid="sub-refname" {...register("referrer_name")} className="rounded-sm" /></Field>
            <Field label="Referrer Mobile"><Input data-testid="sub-refmobile" {...register("referrer_mobile")} className="rounded-sm font-mono" /></Field>
            <Field label="Occasion (gift)" full>
              <Input data-testid="sub-occasion" {...register("occasion")} className="rounded-sm" placeholder="Birthday Gift, Anniversary Gift, etc." />
            </Field>
            <Field label="Preferred Postage Class" full>
              <Select value={v.postage_class || "Registered Periodical"} onValueChange={(x) => setValue("postage_class", x)}>
                <SelectTrigger data-testid="sub-postage" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Registered Periodical">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">Registered Periodical <span className="ml-1 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700">Default · No extra cost</span></span>
                      <span className="text-[11px] text-slate-500">Concessional rate for RNI-registered magazines · slowest · tracked at bundle level</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Book Post">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">Book Post <span className="ml-1 rounded-sm bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-600">~ ₹4–8 / piece</span></span>
                      <span className="text-[11px] text-slate-500">Bulk printed-matter rate · no tracking · suitable for unregistered titles</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Registered Post">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">Registered Post <span className="ml-1 rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">+ ₹25–35 / piece</span></span>
                      <span className="text-[11px] text-slate-500">Acknowledgement-of-delivery slip · slower but safer</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Speed Post">
                    <div className="flex flex-col items-start">
                      <span className="font-medium">Speed Post <span className="ml-1 rounded-sm bg-rose-50 px-1.5 py-0.5 text-[10px] text-rose-700">+ ₹40–80 / piece (premium)</span></span>
                      <span className="text-[11px] text-slate-500">Fastest · ~2-3 days · full tracking · use only if subscriber asks &amp; pays extra</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {v.postage_class && v.postage_class !== "Registered Periodical" && (
                <div className={`mt-2 rounded-sm border px-3 py-2 text-[11px] ${
                  v.postage_class === "Speed Post" ? "border-rose-200 bg-rose-50 text-rose-800"
                  : v.postage_class === "Registered Post" ? "border-amber-200 bg-amber-50 text-amber-900"
                  : "border-slate-200 bg-slate-50 text-slate-700"
                }`}>
                  <span className="font-semibold">Heads-up:</span>{" "}
                  {v.postage_class === "Speed Post"
                    ? "Speed Post adds ~₹40–80 per issue. Make sure the subscriber has paid the additional dispatch surcharge upfront, or has signed up to an upgraded plan that includes it."
                    : v.postage_class === "Registered Post"
                    ? "Registered Post adds ~₹25–35 per issue. Subscriber should pay the additional dispatch surcharge upfront."
                    : "Book Post is slightly costlier than Registered Periodical and offers no tracking — choose only if your title isn't RNI-registered."}
                </div>
              )}
            </Field>
          </div>
        </Section>

        <Section title="Payment">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Method" required>
              <Select value={v.payment_method || ""} onValueChange={(x) => setValue("payment_method", x)}>
                <SelectTrigger data-testid="sub-paymethod" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Amount Paid" required><Input type="number" step="0.01" data-testid="sub-payamount" {...register("payment_amount")} className="rounded-sm font-mono" /></Field>
            {v.payment_method === "Cheque" && (
              <>
                <Field label="Cheque No." required><Input data-testid="sub-chequeno" {...register("cheque_number")} className="rounded-sm font-mono" /></Field>
                <Field label="Bank Name" required full><Input data-testid="sub-chequebank" {...register("cheque_bank")} className="rounded-sm" /></Field>
              </>
            )}
            {v.payment_method === "UPI" && (
              <Field label="UPI Reference"><Input data-testid="sub-upi" {...register("upi_ref")} className="rounded-sm font-mono" placeholder="UPI/123456789" /></Field>
            )}
            <Field label="Receipt / Screenshot" full hint="Image or PDF, max 2MB">
              <div className="flex items-center gap-3">
                {v.receipt_base64 ? (
                  <div className="flex items-center gap-2">
                    <span className="rounded-sm bg-emerald-50 px-2 py-1 text-xs text-emerald-700">Receipt attached</span>
                    <Button type="button" size="sm" variant="outline" onClick={() => setValue("receipt_base64", null)} className="rounded-sm">Remove</Button>
                  </div>
                ) : (
                  <input type="file" accept="image/*,.pdf" onChange={onReceipt} data-testid="sub-receipt-file" className="block text-sm file:mr-3 file:rounded-sm file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-white" />
                )}
              </div>
            </Field>
          </div>
        </Section>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/subscribers")} className="rounded-sm">Cancel</Button>
          <Button type="submit" disabled={submitting} data-testid="sub-submit-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
            <Save size={14} className="mr-1.5" />
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Create subscriber"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="mb-4 border-b border-slate-200 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, required, hint, error, full, children }) {
  return (
    <div className={full ? "sm:col-span-3" : ""}>
      <Label className="mb-1 block text-sm font-medium text-slate-700">{label} {required && <Required />}</Label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
    </div>
  );
}
